import { CSSProperties, ReactNode } from 'react';
import {
    Box, Table, TableContainer, Paper, TableHead, TableRow, TableCell, Checkbox,
    TableBody, CircularProgress, Typography, Chip, IconButton, Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLanguage } from '../../../../context/LanguageContext';
import { ImportedList } from '../../ListImport/types';

type ListId = ImportedList['LIST_ID'];

interface ListTypeConfig {
    translationKey: string;
    color: 'primary' | 'info' | 'success' | 'warning' | 'secondary';
}

const PERSON_LIST_TYPE: ListTypeConfig = { translationKey: 'person', color: 'primary' };

const FLAGGED_LIST_TYPES: Array<{ flag: keyof ImportedList } & ListTypeConfig> = [
    { flag: 'IS_EMAIL_LIST', translationKey: 'email', color: 'success' },
    { flag: 'IS_CELL_PHONE_LIST', translationKey: 'mobilephone', color: 'warning' },
    { flag: 'IS_WALLET_LIST', translationKey: 'wallet', color: 'secondary' },
    { flag: 'IS_IDENTIFIER_LIST', translationKey: 'card', color: 'info' },
];

const getListType = (list: ImportedList): ListTypeConfig =>
    FLAGGED_LIST_TYPES.find(type => list[type.flag] === '1') ?? PERSON_LIST_TYPE;

const ROW_HEIGHT = 68;
const HEADER_HEIGHT = 56;
const PADDING_HEIGHT = 10;
const COLUMN_WIDTHS = ['48px', '35%', '20%', '12%', '18%', '10%'];

const labelSx: CSSProperties = {
    maxWidth: 800,
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
};

interface ImportedListsTableProps {
    lists: ImportedList[];
    loading: boolean;
    rowsPerPage: number;
    selectedIds: ListId[];
    onToggleRow: (id: ListId) => void;
    onToggleAll: () => void;
    onView: (list: ImportedList) => void;
    onDelete: (list: ImportedList) => void;
}

function ImportedListsTable({ lists, loading, rowsPerPage, selectedIds, onToggleRow, onToggleAll, onView, onDelete }: ImportedListsTableProps) {
    const { t, formatLocale } = useLanguage();
    const displayedRows = loading ? rowsPerPage : Math.max(1, lists.length);
    const minHeight = HEADER_HEIGHT + displayedRows * ROW_HEIGHT + PADDING_HEIGHT;

    const renderMessageRow = (content: ReactNode) => (
        <TableRow>
            <TableCell colSpan={COLUMN_WIDTHS.length} align="center" sx={{ py: 5 }}>{content}</TableCell>
        </TableRow>
    );

    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', minHeight: `${minHeight}px` }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                    {COLUMN_WIDTHS.map((width, index) => <col key={index} style={{ width }} />)}
                </colgroup>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell padding="checkbox">
                            <Checkbox
                                checked={lists.length > 0 && selectedIds.length === lists.length}
                                indeterminate={selectedIds.length > 0 && selectedIds.length < lists.length}
                                onChange={onToggleAll}
                            />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('pages.clients.queryBuilder.myListsTab.list')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('pages.clients.queryBuilder.myListsTab.importDate')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">{t('pages.clients.queryBuilder.myListsTab.accountsCount')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">{t('pages.clients.queryBuilder.myListsTab.listType')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">{t('pages.clients.queryBuilder.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading
                        ? renderMessageRow(<CircularProgress />)
                        : lists.length === 0
                            ? renderMessageRow(<Typography color="text.secondary">{t('pages.clients.queryBuilder.myListsTab.noListFound')}</Typography>)
                            : lists.map(list => {
                                const listType = getListType(list);
                                return (
                                    <TableRow key={list.LIST_ID} hover>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedIds.includes(list.LIST_ID)} onChange={() => onToggleRow(list.LIST_ID)} />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={list.LABEL} arrow>
                                                <span style={labelSx}>{list.LABEL}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {list.IMPORT_DATE ? new Date(list.IMPORT_DATE).toLocaleString(formatLocale) : '-'}
                                        </TableCell>
                                        <TableCell align="center">{list.TOTAL_ACCOUNTS || 0}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                variant="outlined"
                                                color={listType.color}
                                                size="small"
                                                label={t(`pages.clients.importList.${listType.translationKey}`)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" gap={1} justifyContent="center">
                                                <Tooltip title={t('common.details')}>
                                                    <IconButton size="small" color="primary" onClick={() => onView(list)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('common.delete')}>
                                                    <IconButton size="small" color="error" onClick={() => onDelete(list)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default ImportedListsTable;
